<template>
  <div v-rect-loading="loading" class="update-mobile-form">
    <div class="form-line" :class="getErrorClass(message.phone)">
      <el-input
        v-model="formData.phone"
        class="line-input"
        :placeholder="$t('login.common.placeholder.phone')"
        @focus="clearErrorMessage(message.phone)"
        @blur="validate(message.phone)"
        @change="validate(message.phone)"
      >
        <template #prepend>
          <el-select
            v-model="selectedCountryCode"
            class="country-code"
            filterable
            :popper-class="`country-code-popper ${isMobile ? 'is-mobile-el-select' : ''}`"
            :popper-append-to-body="false"
            :filter-method="searchCountryCode"
            @visible-change="resetSearchedCountryCodeList"
            @change="handleCountryCodeChange"
          >
            <el-option
              v-for="(code, index) in searchedCountryCodeList"
              class="country-code-option"
              :key="index"
              :value="code.value"
            >
              <span class="country-name">{{ code.countryName }}</span>
              <span class="country-code-label">{{ code.value }}</span>
            </el-option>
          </el-select>
        </template>
      </el-input>
      <div v-show="message.phone.show" class="line-tip">{{ selectLabelI18n(message.phone, 'text') }}</div>
    </div>
    <div v-if="formData.countryCode === CHINA_COUNTRY_CODE && isCandidate" class="form-line" :class="getErrorClass(message.verifyCode)">
      <PhoneVerifyCodeInput
        v-model="formData.verifyCode"
        class="line-input"
        ref="verifyCodeInput"
        appendToBody
        :captchaIndex="captchaIndex"
        :phone="verifyCodePhone"
        :placeholder="$t('login.common.label.verifyCode')"
        @focus="clearErrorMessage(message.verifyCode)"
        @blur="validate(message.verifyCode)"
        @change="validate(message.verifyCode)"
        @sendVerifySuccess="handelSendVerifySuccess"
      />
      <div v-show="message.verifyCode.show" class="line-tip">{{ selectLabelI18n(message.verifyCode, 'text') }}</div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { validatePhone } from '@/utils/validator'
import { mapActions } from 'vuex'
import { getI18nPropertyValue, selectLabelI18n } from '@/i18n'
import { CHINA_COUNTRY_CODE } from '@/constant/common'
import PhoneVerifyCodeInput from '@/components/formEditor/phoneVerifyCodeInput.vue'
import { deviceType } from '@/utils/common'

export default {
  components: {
    PhoneVerifyCodeInput
  },
  props: {
    phone: {
      type: String
    },
    isShow: {
      type: Boolean,
      default: false
    },
    countryCode: {
      type: String
    },
    jobId: {
      default: null
    },
    captchaIndex: {
      type: Number,
      required: true
    },
    isCandidate: {
      type: Boolean,
      default: false
    },
    companyId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      isMobile: deviceType.isMobile,
      i18nPrefix: 'admin.application.candidate',
      CHINA_COUNTRY_CODE,
      selectLabelI18n,
      loading: false,
      hasSentVerifyCode: false,
      selectedCountryCode: null,
      formData: {
        phone: null,
        countryCode: CHINA_COUNTRY_CODE,
        verifyCode: null,
        captcha: null
      },
      message: {
        countryCode: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        },
        phone: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        },
        verifyCode: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        }
      },
      countryCodeList: [],
      searchedCountryCodeList: [],
      hasMounted: false
    }
  },
  computed: {
    verifyCodePhone () {
      const error = validatePhone(null, this.formData.phone)
      if (error) {
        return null
      }
      return this.formData.phone
    }
  },
  created () {
    this.initCountryCodeList()
  },
  mounted () {
    this.fetchStatus()
    this.hasMounted = true
  },
  methods: {
    ...mapActions('common', ['getCountryCodeList']),
    ...mapActions('personal', ['updatePersonalResumeMobile']),
    getErrorClass (message) {
      return message && message.show && message.isError && 'has-error'
    },
    setErrorMessage (message, textI18nKey, getter) {
      if (getter) {
        Object.defineProperty(message, 'text', {
          configurable: true,
          enumerable: true,
          get: getter
        })
      } else {
        message.text = null
      }
      message.isError = true
      message.textI18nKey = textI18nKey
      message.show = true
    },
    clearErrorMessage (message) {
      message.show = false
      message.isError = false
      message.text = null
      message.textI18nKey = null
    },
    validate (message) {
      if (!message) {
        return _.chain(this.message).map(this.validate).includes(false).value()
      }
      let hasError = false
      let textI18nKey = null
      const getter = null
      if (message === this.message.phone) {
        if (this.formData.phone) {
          if (this.formData.countryCode === CHINA_COUNTRY_CODE) {
            if (this.formData.phone === this.phone) {
              hasError = true
              textI18nKey = 'login.common.message.phoneNotChange'
            } else if (validatePhone(null, this.formData.phone)) {
              hasError = true
              textI18nKey = 'common.message.invalidMobile'
            }
          }
        } else {
          hasError = true
          textI18nKey = 'login.common.placeholder.phone'
        }
      } else if (this.formData.countryCode === CHINA_COUNTRY_CODE && message === this.message.verifyCode && this.isCandidate) {
        if (this.hasSentVerifyCode) {
          if (this.formData.verifyCode && !/^\d{0,6}$/.test(this.formData.verifyCode)) {
            this.formData.verifyCode = null
          } else if (!this.formData.verifyCode) {
            hasError = true
            textI18nKey = 'login.common.placeholder.verifyCode'
          }
        } else {
          hasError = true
          textI18nKey = 'login.common.message.getVerifyCodeFirst'
        }
      }
      if (hasError) {
        this.setErrorMessage(message, textI18nKey, getter)
        return false
      } else {
        this.clearErrorMessage(message)
        return true
      }
    },
    searchCountryCode (text) {
      this.selectedCountryCode = text
      if (!text) {
        this.searchedCountryCodeList = this.countryCodeList
        return
      }
      text = text.toLocaleLowerCase()
      this.searchedCountryCodeList = _.filter(this.countryCodeList, code => _.includes(code.label.toLocaleLowerCase(), text))
    },
    resetSearchedCountryCodeList (visible) {
      if (!visible) {
        this.selectedCountryCode = this.formData.countryCode
        this.searchedCountryCodeList = this.countryCodeList
      }
    },
    handleCountryCodeChange (params) {
      this.formData.countryCode = params
      this.validate(this.message.phone)
    },
    async initCountryCodeList () {
      this.loading = true
      try {
        const resp = await this.getCountryCodeList()
        if (resp) {
          this.countryCodeList = resp.country_code_list.map(item => {
            return {
              countryCode: item.country_code,
              get countryName () {
                return getI18nPropertyValue(item, 'country_name_cn', 'country_name_en')
              },
              get label () {
                return `${this.countryName} +${this.countryCode}`
              },
              value: `+${item.country_code}`
            }
          })
          this.searchedCountryCodeList = this.countryCodeList
        }
      } finally {
        this.loading = false
      }
    },
    handleCancelCaptchaDialog () {
      this.clickDisable = false
    },
    handelSendVerifySuccess () {
      this.hasSentVerifyCode = true
    },
    async onSubmit () {
      const isValid = this.validate()
      if (isValid) {
        return
      }
      this.loading = true
      const formData = this.formData
      const params = {
        mobile: formData.phone,
        sms_validation_code: formData.verifyCode,
        country_code: formData.countryCode,
        job_id: this.jobId,
        company_id: this.companyId
      }
      try {
        if (this.isCandidate) {
          const resp = await this.updatePersonalResumeMobile(params)
          if (resp) {
            this.$refs.verifyCodeInput?.resetVerifyCode()
            this.$emit('success', this.formData)
            this.$message.success(this.$t('login.common.message.updateSuccess'))
          }
        } else {
          this.$refs.verifyCodeInput?.resetVerifyCode()
          this.$emit('success', this.formData)
        }
      } finally {
        this.loading = false
      }
    },
    fetchStatus () {
      this.hasSentVerifyCode = false
      this.formData.verifyCode = null
      if (this.phone) {
        this.formData.phone = this.phone
      }
      this.formData.countryCode = this.countryCode || CHINA_COUNTRY_CODE
      this.selectedCountryCode = this.formData.countryCode
      _.forEach(this.message, this.clearErrorMessage)
    }
  },
  watch: {
    isShow (val) {
      if (!val || !this.hasMounted) {
        return
      }
      this.fetchStatus()
    }
  }
}
</script>

<style lang="scss" scoped>
.update-mobile-form {
  margin-top: 24px;

  .form-line {
    min-height: 40px;
    margin-bottom: 24px;

    &.has-error {
      margin-bottom: 6px;

      .line-tip {
        padding-left: 0;
        color: #f56c6c;
      }

      .line-input {
        ::v-deep {
          > .el-input__inner {
            border-color: #f56c6c;
          }

          .verify-code {
            .el-input__inner {
              border-color: #f56c6c;
            }
          }

          .el-input__inner:hover + .el-input-group__prepend,
          .el-input__inner:hover + .el-input-group__append,
          .el-input-group__prepend,
          .el-input-group__append {
            border-color: #f56c6c;
          }
        }
      }
    }

    .line-input {
      ::v-deep {
        .el-input__inner {
          height: 40px;
          line-height: 40px;
          border-radius: 2px;
          padding-left: 14px;
          padding-right: 35px;

          &:focus {
            border-color: #0096cc;
          }
        }

        .el-input__suffix {
          right: 14px;
        }

        .el-input-group__prepend {
          border-top-left-radius: 2px;
          border-bottom-left-radius: 2px;
          background: initial;
          transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)
        }

        .el-input-group__append {
          border-top-right-radius: 2px;
          border-bottom-right-radius: 2px;
          background: initial;
          transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)
        }
      }

      .country-code {
        width: 90px;
        color: #606266;

        ::v-deep {
          .el-input {

            .el-input__inner {
              padding-right: 30px;

              &:focus {
                border-color: transparent;
              }
            }

            .el-input__suffix {
              right: 5px;
            }
          }
        }
      }
    }

    .verify-code {
      &::v-deep {
        .el-input__inner {
          border-right: 0;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        .el-input-group__append {
          padding-left: 0;
          padding-right: 14px;
        }
      }

      .send-verify-code {
        color: #0096cc;
        cursor: pointer;

        &.has-sent {
          cursor: default;
        }
      }
    }

    .line-tip {
      padding-left: 15px;
      font-size: 12px;
      line-height: 18px;
    }
  }
}

.country-code-option {
  display: flex;
  justify-content: space-between;

  .country-name {
    flex: 1;
  }

  .country-code-label {
    margin-left: 5px;
  }
}
</style>

<style lang="scss">
// to fix 在ios设备中，el-select组件下拉框，点击2次才能选中问题。
.is-mobile-el-select {
  .el-scrollbar {
    .el-scrollbar__bar {
      opacity: 1 !important;
    }
  }
}
</style>
